/**
 * Into: the campaign group of this country the copy lands in — the same
 * groups (crown, label, what each holds) as the board's switcher — and
 * whether it already has a master config. v2 (26/10/2026): any target
 * country; one added without a group gets the copied group's twin by name
 * once its groups load (else the person picks). 27/10/2026: one quiet line under the tabs.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useEffect } from 'react';
import { Crown, Layers } from 'lucide-react';

import { SearchableSelect, type SearchableSelectOption } from '@/components/ui';
import { entryFacts, type GroupEntry, middleTruncate } from '@/features/drop-generator/components/campaign/campaign-scope';
import type { GroupCopyClip } from '@/features/drop-generator/components/group-copy/clipboard/group-copy-clipboard';

import { twinGroup, useCountryGroups } from './use-country-groups';

interface RowProps {
  groups: GroupEntry[];
  target: GroupEntry | null;
  onPick: (key: string) => void;
}

const Mark: FC<{ entry: GroupEntry }> = ({ entry }) =>
  entry.master ? <Crown className="h-3.5 w-3.5 shrink-0" style={{ color: entry.master.color }} /> : <Layers className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />;

function groupOption(entry: GroupEntry): SearchableSelectOption<string> {
  const optionContent = (
    <span className="flex min-w-0 items-start gap-2" title={entry.fullLabel}>
      <span className="mt-0.5"><Mark entry={entry} /></span>
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-medium">{entry.label}</span>
        <span className="truncate text-xs text-muted-foreground">{entryFacts(entry)}</span>
      </span>
    </span>
  );
  const triggerContent = (
    <span className="flex min-w-0 items-center gap-2">
      <Mark entry={entry} />
      <span className="truncate font-medium">{middleTruncate(entry.label, 40)}</span>
    </span>
  );
  return { value: entry.key, label: entry.fullLabel, optionContent, triggerContent };
}

const targetFact = (target: GroupEntry) => (target.master ? 'has its own master config' : 'no master config yet — the copy becomes it');

export const IntoRow: FC<RowProps> = ({ groups, target, onPick }) => (
  <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
    <span className="shrink-0 text-xs font-medium text-muted-foreground">Into campaign group</span>
    <div className="min-w-0 flex-1 basis-60 sm:max-w-sm">
      <SearchableSelect options={groups.map(groupOption)} value={target?.key ?? ''} onChange={onPick} placeholder="Pick a campaign group…" searchPlaceholder="Search a campaign group…" className="h-9" />
    </div>
    {target ? <span className="min-w-0 text-xs text-muted-foreground">{targetFact(target)}</span> : null}
  </div>
);

interface Props {
  clip: GroupCopyClip;
  countryKey: string;
  countryName: string;
  /** The board's own groups when this is the board's country; absent = read them. */
  boardGroups?: GroupEntry[];
  groupKey: string | null;
  onPick: (key: string) => void;
}

const Read: FC<Omit<Props, 'boardGroups'>> = ({ clip, countryKey, groupKey, onPick, countryName }) => {
  const { groups, loaded, failed } = useCountryGroups(countryKey, clip);
  useEffect(() => {
    if (groupKey !== null || !loaded) return;
    const twin = twinGroup(groups, clip.label);
    if (twin) onPick(twin);
  }, [groupKey, loaded, groups, clip.label, onPick]);
  if (failed) return <p className="text-sm text-destructive">{countryName} has no Automation plan to paste into.</p>;
  if (!loaded) return <p className="text-sm text-muted-foreground">Reading {countryName}&apos;s campaign groups…</p>;
  return <IntoRow groups={groups} target={groups.find((g) => g.key === groupKey) ?? null} onPick={onPick} />;
};

export const TargetInto: FC<Props> = ({ boardGroups, ...props }) =>
  boardGroups ? (
    <IntoRow groups={boardGroups} target={boardGroups.find((g) => g.key === props.groupKey) ?? null} onPick={props.onPick} />
  ) : (
    <Read {...props} />
  );
